<script setup lang="ts">
import { computed, ref } from 'vue'
import type { ProductOwnerRun } from '../stores/product_owner_runs'
import { productOwnerRunStatusBadgeClass, productOwnerRunStatusLabel } from '../composables/productOwnerRunStatus'
import {
  productOwnerCadenceStateBadgeClass,
  productOwnerCadenceStateSinceLabel,
} from '../composables/productOwnerCadenceState'
import {
  cadenceStateTitle,
  cadenceTitle,
  formatCadence,
  formatNextCheck,
} from '../composables/productOwnerRunCadenceLabels'
import { formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import StatusPill from '../components/StatusPill.vue'

// One run's card in /admin/product-owner-runs (#916, card layout #921).
// Split out of AdminProductOwnerRuns.vue in #925 so the list can grow to
// many runs without an endless scroll of full detail text: the at-a-glance
// info (repo, status, started time, cost, merged PRs) always stays visible,
// and the longer summary/error text collapses behind a toggle — following
// the same expand-affordance shape as ActivityRow.vue (chevron + toggle
// button) and PromptLibraryRow.vue (extracted list-item component).
//
// `defaultExpanded` seeds the local `expanded` ref once at setup — the
// parent computes it from "is this the most-recently-started run" and
// passes it down as a plain prop rather than this component reaching into
// the store itself, keeping this a dumb, reusable list-item component.
const props = withDefaults(
  defineProps<{
    run: ProductOwnerRun
    defaultExpanded?: boolean
  }>(),
  {
    defaultExpanded: false,
  },
)

const expanded = ref(props.defaultExpanded)
const now = useNow()

// A stale `running` row (backend-computed, same cutoff as
// ProductOwnerRun.busy?/.reap_stale!) is presumed abandoned rather than
// genuinely in flight — most commonly an agent-box redeploy landing
// mid-session. Overriding the plain "Running" badge here is what makes
// that distinguishable from a normal in-progress run at a glance, instead
// of only being findable by comparing "Started" against the wall clock.
// An overrunning row (#1739) is the opposite case: genuinely live, just
// past the repo's own cadence interval, so the heartbeat is skipping ticks.
const overrun = computed(() => !props.run.stale && props.run.next_check?.state === 'overrun')
const statusLabel = computed(() =>
  props.run.stale ? 'Stale' : overrun.value ? 'Overrunning' : productOwnerRunStatusLabel(props.run.status),
)
const statusBadgeClass = computed(() =>
  props.run.stale
    ? 'bg-red-500/15 text-red-300/90'
    : overrun.value
      ? 'bg-amber-500/15 text-amber-300/90'
      : productOwnerRunStatusBadgeClass(props.run.status),
)
const staleTitle =
  'This run never finished — likely abandoned by an agent-box restart mid-session. ' +
  'A reaper job finalizes rows like this automatically; this is just a stale snapshot.'
const overrunTitle =
  'Still running, but longer than this repo’s cadence interval — the heartbeat skips its ticks until this run finishes.'
const statusTitle = computed(() => (props.run.stale ? staleTitle : overrun.value ? overrunTitle : undefined))
const hasDetail = computed(() => !!(props.run.summary || props.run.error || props.run.auto_merge_notes))

// Compact metadata line (#931): was a 3-row dt/dd grid burning a full
// row per value; joined here into one flex-wrapped, dot-separated
// line — same idiom as UsageView.vue's per-model cost breakdown — so a
// growing run list doesn't force one scroll-page per handful of cards.
const mergedPrsLabel = computed(() => {
  if (props.run.merged_pr_numbers.length === 0) return '—'
  return props.run.merged_pr_numbers.map((n) => `#${n}`).join(', ')
})

// The human-filed backlog count's after-minus-before delta (#1623) — null
// whenever backlog_after wasn't recorded (older rows predating this field,
// or a run that failed before reaching its own end-of-run count).
function formatDelta(delta: number | null): string {
  if (delta == null) return '—'
  return delta > 0 ? `+${delta}` : String(delta)
}

// Level (backlog_after) plus delta for one bucket; the level is '—' too when
// the run never recorded its end-of-run count.
function backlogLabel(level: number | null, delta: number | null): string {
  return `${level ?? '—'} (${formatDelta(delta)})`
}

const backlogDeltaLabel = computed(() => backlogLabel(props.run.backlog_after, props.run.backlog_delta))
const totalBacklogLabel = computed(() => backlogLabel(props.run.total_backlog_after, props.run.total_backlog_delta))

function toggleExpand(): void {
  expanded.value = !expanded.value
}

function formatCost(cents: number | null): string {
  if (cents == null) return '—'
  return `$${(cents / 100).toFixed(2)}`
}

// Day.month.year, 24-hour clock (#974) — same European format every
// other timestamp in the app uses via useFormattedDate, rather than the
// browser-locale-dependent (and US-style AM/PM) Date#toLocaleString.
function formatTime(iso: string | null): string {
  return iso ? formatDate(iso, now.value) : '—'
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

// How long the run actually took (#990) — `started_at`/`finished_at` are
// both written in the same `record_run!` call at the end of
// ProductOwnerRunJob, so a completed row always has both; guarded anyway
// since older/malformed rows can still have a null `finished_at` (see the
// cancelled-run fixture in the spec).
function formatDuration(startedAt: string | null, finishedAt: string | null): string {
  if (props.run.running_for_seconds != null) return `${formatSeconds(props.run.running_for_seconds)} so far`
  if (!startedAt || !finishedAt) return '—'
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime()
  if (!Number.isFinite(ms) || ms < 0) return '—'
  return formatSeconds(Math.round(ms / 1000))
}

function formatSeconds(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) return `${hours}h ${pad(minutes)}m`
  if (minutes > 0) return `${minutes}m ${pad(seconds)}s`
  return `${seconds}s`
}
</script>

<template>
  <li
    class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-3"
    :data-testid="`product-owner-run-${run.id}`"
  >
    <div class="flex items-center gap-3 flex-wrap">
      <h2 class="text-base font-semibold text-white m-0 font-mono break-all">{{ run.repo }}</h2>
      <StatusPill
        :label="statusLabel"
        :color-class="statusBadgeClass"
        :title="statusTitle"
        :data-testid="`product-owner-run-status-${run.id}`"
      />
      <StatusPill
        v-if="run.cadence"
        :label="productOwnerCadenceStateSinceLabel(run.cadence.state, run.cadence.state_entered_at, now)"
        :color-class="productOwnerCadenceStateBadgeClass(run.cadence.state)"
        :title="cadenceStateTitle(run.cadence, now)"
        :data-testid="`product-owner-run-cadence-state-${run.id}`"
      />
    </div>

    <div
      class="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-white/70"
      :data-testid="`product-owner-run-meta-${run.id}`"
    >
      <span class="tabular-nums"><span class="text-white/40">Started</span> {{ formatTime(run.started_at) }}</span>
      <span aria-hidden="true" class="text-white/30">·</span>
      <span class="tabular-nums"
        ><span class="text-white/40">Took</span> {{ formatDuration(run.started_at, run.finished_at) }}</span
      >
      <span aria-hidden="true" class="text-white/30">·</span>
      <span
        class="tabular-nums"
        title="Estimated cost if this run were metered by API usage — the agent box authenticates via a flat-rate Claude subscription, so nothing is actually billed per token"
        ><span class="text-white/40">Cost</span> {{ formatCost(run.cost_cents) }}</span
      >
      <span aria-hidden="true" class="text-white/30">·</span>
      <span><span class="text-white/40">Merged</span> {{ mergedPrsLabel }}</span>
      <span aria-hidden="true" class="text-white/30">·</span>
      <span class="tabular-nums" :data-testid="`product-owner-run-backlog-delta-${run.id}`">
        <span class="text-white/40">Backlog</span> {{ backlogDeltaLabel }}
        <span class="text-white/40">human</span>
      </span>
      <span aria-hidden="true" class="text-white/30">·</span>
      <span class="tabular-nums" :data-testid="`product-owner-run-total-backlog-delta-${run.id}`">
        <span class="text-white/40">Backlog</span> {{ totalBacklogLabel }}
        <span class="text-white/40">total</span>
      </span>
      <template v-if="run.next_check">
        <span aria-hidden="true" class="text-white/30">·</span>
        <span class="tabular-nums" :data-testid="`product-owner-run-next-check-${run.id}`">
          <span class="text-white/40">Next check</span> {{ formatNextCheck(run.next_check, now) }}
        </span>
      </template>
      <template v-if="run.cadence">
        <span aria-hidden="true" class="text-white/30">·</span>
        <span
          class="tabular-nums"
          :title="cadenceTitle(run.cadence)"
          :data-testid="`product-owner-run-cadence-${run.id}`"
        >
          <span class="text-white/40">Cadence</span> {{ formatCadence(run.cadence) }}
        </span>
      </template>
    </div>

    <!-- Toggle for the summary/error text below. `min-h-11` (44px) keeps
         the tap target comfortable on a phone even though the label text
         itself is small — this page was previously flagged for cramped
         mobile controls (#921) and this is a new interactive element. -->
    <button
      v-if="hasDetail"
      type="button"
      class="flex items-center gap-1.5 self-start min-h-11 px-1 text-xs text-white/60 hover:text-white active:text-white bg-transparent border-0 cursor-pointer rounded"
      :aria-expanded="expanded"
      :data-testid="`product-owner-run-toggle-${run.id}`"
      @click="toggleExpand"
    >
      <span
        aria-hidden="true"
        class="inline-block w-3 transition-transform select-none"
        :class="{ 'rotate-90': expanded }"
        >›</span
      >
      {{ expanded ? 'Hide details' : 'Show details' }}
    </button>

    <p
      v-if="expanded && (run.summary || run.error)"
      class="text-xs text-white/70 m-0 border-t border-white/10 pt-3 whitespace-pre-line break-words"
      :data-testid="`product-owner-run-detail-${run.id}`"
    >
      {{ run.summary || run.error }}
    </p>

    <!-- Separate from summary/error (#960): a skipped-PR reason from the
         auto-merge step, not the persona's own run — surfaced even when a
         run otherwise has no summary/error to show at all. -->
    <p
      v-if="expanded && run.auto_merge_notes"
      class="text-xs text-amber-300/80 m-0 border-t border-white/10 pt-3 whitespace-pre-line break-words"
      :data-testid="`product-owner-run-auto-merge-notes-${run.id}`"
    >
      Auto-merge: {{ run.auto_merge_notes }}
    </p>
  </li>
</template>
